import React, { useState } from "react";
import { ChevronRight, Globe, BookOpen, Stethoscope, Calculator } from "lucide-react";
import { motion } from "motion/react";
import DermatologyNewsViewer from "./DermatologyNewsViewer";
import HumanFactorsViewer from "./HumanFactorsViewer";
import { Brain } from "lucide-react";
import ClinicalGuidelinesViewer from "./ClinicalGuidelinesViewer";
import ImportantWebsitesViewer from "./ImportantWebsitesViewer";
import PatientLeafletsViewer from "./PatientLeafletsViewer";
import { FileText } from "lucide-react";

export default function DoctorsPortalView({ onBack }: { onBack: () => void }) {
  const [showNews, setShowNews] = useState(false);
  const [showGuidelines, setShowGuidelines] = useState(false);
  const [showHumanFactors, setShowHumanFactors] = useState(false);
  const [showImportantWebsites, setShowImportantWebsites] = useState(false);
  const [showLeaflets, setShowLeaflets] = useState(false);

  if (showNews) {
    return <DermatologyNewsViewer onBack={() => setShowNews(false)} />;
  }

  if (showGuidelines) {
    return <ClinicalGuidelinesViewer onBack={() => setShowGuidelines(false)} />;
  }

  if (showHumanFactors) {
    return <HumanFactorsViewer onBack={() => setShowHumanFactors(false)} />;
  }

  if (showImportantWebsites) {
    return <ImportantWebsitesViewer onBack={() => setShowImportantWebsites(false)} />;
  }

  if (showLeaflets) {
    return <PatientLeafletsViewer onBack={() => setShowLeaflets(false)} />;
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col pt-16 md:pt-24 pb-8 md:pb-12 px-4 relative overflow-hidden" dir="rtl">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-[50vh] md:h-96 bg-slate-900 rounded-b-[4rem] z-0" />
      
      <div className="max-w-6xl mx-auto w-full relative z-10 flex-1 flex flex-col">
        <button 
          onClick={onBack}
          className="self-end mb-8 bg-white/10 hover:bg-white/20 text-white rounded-full py-2 px-5 flex items-center gap-2 transition-all border border-white/20"
        >
          <ChevronRight className="w-4 h-4" />
          <span className="text-sm font-bold">العودة للرئيسية</span>
        </button>

        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center p-4 bg-indigo-500/20 rounded-full mb-4">
            <Stethoscope className="w-10 h-10 text-indigo-400" />
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white tracking-tight">البوابة الطبية</h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:p-6 mt-4">
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={() => setShowNews(true)}
            className="bg-white rounded-3xl p-4 md:p-6 shadow-xl border border-slate-100 hover:shadow-2xl hover:-translate-y-1 transition-all cursor-pointer group relative overflow-hidden"
          >
            <div className="absolute top-0 left-0 w-32 h-32 bg-indigo-50 rounded-br-full -z-10 group-hover:scale-110 transition-transform" />
            <div className="w-12 h-12 md:w-14 md:h-14 bg-indigo-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-indigo-200">
              <Globe className="w-6 h-6 md:w-7 md:h-7 text-white" />
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-slate-800 mb-3">أحدث الأخبار الطبية</h3>
            <p className="text-slate-500 leading-relaxed text-sm mb-6">
              تغطية حية لأحدث الأبحاث السريرية والعلاجات المبتكرة والأخبار في مجال طب الأمراض الجلدية حول العالم.
            </p>
            <div className="flex items-center text-indigo-600 font-bold text-sm gap-2 mt-auto">
              <span>تصفح الأخبار</span>
              <ChevronRight className="w-4 h-4 group-hover:-translate-x-1 transition-transform rotate-180" />
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            onClick={() => setShowLeaflets(true)}
            className="bg-white rounded-3xl p-4 md:p-6 shadow-xl border border-slate-100 hover:shadow-2xl hover:-translate-y-1 transition-all cursor-pointer group relative overflow-hidden flex flex-col justify-between"
          >
            <div className="absolute top-0 left-0 w-32 h-32 bg-emerald-50 rounded-br-full -z-10 group-hover:scale-110 transition-transform" />
            <div>
              <div className="flex items-center justify-between mb-6">
                <div className="w-12 h-12 md:w-14 md:h-14 bg-emerald-600 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-200">
                  <FileText className="w-6 h-6 md:w-7 md:h-7 text-white" />
                </div>
                <span className="bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-200 px-3 py-1 rounded-full text-xs font-black flex items-center gap-1.5 shadow-sm transition-colors">
                  ✨ المجموعة الـ 3 (30 مرجعاً سريرياً)
                </span>
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-slate-800 mb-3">التثقيف الصحي - نشرات معلومات المريض</h3>
              <p className="text-slate-500 leading-relaxed text-sm mb-4">
                نشرات تثقيفية موثقة من مرجع BAD باللغة العربية مع إمكانية عرض وإضافة صور الأمراض الجلدية والمشاركة المباشرة.
              </p>
              <div className="bg-emerald-50/70 border border-emerald-200/70 rounded-2xl p-3 mb-6 text-xs font-bold text-slate-700 flex items-center justify-between shadow-sm">
                <span className="text-emerald-900 font-bold">الموسوعة المحدثة:</span>
                <span className="text-emerald-700 font-black flex items-center gap-1">3 مجموعات متكاملة (30 مرض)</span>
              </div>
            </div>
            <div className="flex items-center text-emerald-600 font-bold text-sm gap-2 mt-auto">
              <span>تصفح النشرات والصور</span>
              <ChevronRight className="w-4 h-4 group-hover:-translate-x-1 transition-transform rotate-180" />
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            onClick={() => setShowGuidelines(true)}
            className="bg-white rounded-3xl p-4 md:p-6 shadow-xl border border-slate-100 hover:shadow-2xl hover:-translate-y-1 transition-all cursor-pointer group relative overflow-hidden"
          >
            <div className="absolute top-0 left-0 w-32 h-32 bg-blue-50 rounded-br-full -z-10 group-hover:scale-110 transition-transform" />
            <div className="w-12 h-12 md:w-14 md:h-14 bg-blue-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-blue-200">
              <BookOpen className="w-6 h-6 md:w-7 md:h-7 text-white" />
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-slate-800 mb-3">البروتوكولات الطبية</h3>
            <p className="text-slate-500 leading-relaxed text-sm mb-6">
              أحدث الدلائل السريرية والبروتوكولات المعتمدة للأمراض الجلدية من NICE وBAD وAAD وEDF.
            </p>
            <div className="flex items-center text-blue-600 font-bold text-sm gap-2 mt-auto">
              <span>عرض البروتوكولات</span>
              <ChevronRight className="w-4 h-4 group-hover:-translate-x-1 transition-transform rotate-180" />
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            onClick={() => window.open('https://play.google.com/store/apps/details?id=com.iqdoctor.app', '_blank')}
            className="bg-white rounded-3xl p-4 md:p-6 shadow-xl border border-slate-100 hover:shadow-2xl hover:-translate-y-1 transition-all cursor-pointer group relative overflow-hidden flex flex-col justify-between"
          >
            <div className="absolute top-0 left-0 w-32 h-32 bg-purple-50 rounded-br-full -z-10 group-hover:scale-110 transition-transform" />
            <div>
              <div className="w-12 h-12 md:w-14 md:h-14 bg-purple-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-purple-200">
                <Calculator className="w-6 h-6 md:w-7 md:h-7 text-white" />
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-slate-800 mb-3">الحاسبات الطبية</h3>
              <p className="text-slate-500 leading-relaxed text-sm mb-6">
                حاسبات سريرية متخصصة (EASI, PASI, SCORAD, BSA) لتقييم شدة الأمراض الجلدية واتخاذ القرارات العلاجية.
              </p>
            </div>
            <div className="flex items-center text-purple-600 font-bold text-sm gap-2 mt-auto">
              <span>فتح الحاسبات</span>
              <ChevronRight className="w-4 h-4 group-hover:-translate-x-1 transition-transform rotate-180" />
            </div>
          </motion.div>
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            onClick={() => setShowHumanFactors(true)}
            className="bg-white rounded-3xl p-4 md:p-6 shadow-xl border border-slate-100 hover:shadow-2xl hover:-translate-y-1 transition-all cursor-pointer group relative overflow-hidden flex flex-col justify-between"
          >
            <div className="absolute top-0 left-0 w-32 h-32 bg-teal-50 rounded-br-full -z-10 group-hover:scale-110 transition-transform" />
            <div>
              <div className="w-12 h-12 md:w-14 md:h-14 bg-teal-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-teal-200">
                <Brain className="w-6 h-6 md:w-7 md:h-7 text-white" />
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-slate-800 mb-3">التعليم الطبي: العوامل البشرية</h3>
              <p className="text-slate-500 leading-relaxed text-sm mb-6">
                مفاهيم حيوية (Human Factors) وأدوات سريرية لتقليل الأخطاء الطبية، تحسين التواصل، ورفع مستوى السلامة في بيئة العمل.
              </p>
            </div>
            <div className="flex items-center text-teal-600 font-bold text-sm gap-2 mt-auto">
              <span>تصفح الكروت التعليمية</span>
              <ChevronRight className="w-4 h-4 group-hover:-translate-x-1 transition-transform rotate-180" />
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            onClick={() => setShowImportantWebsites(true)}
            className="bg-white rounded-3xl p-4 md:p-6 shadow-xl border border-slate-100 hover:shadow-2xl hover:-translate-y-1 transition-all cursor-pointer group relative overflow-hidden flex flex-col justify-between"
          >
            <div className="absolute top-0 left-0 w-32 h-32 bg-rose-50 rounded-br-full -z-10 group-hover:scale-110 transition-transform" />
            <div>
              <div className="w-12 h-12 md:w-14 md:h-14 bg-rose-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-rose-200">
                <Globe className="w-6 h-6 md:w-7 md:h-7 text-white" />
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-slate-800 mb-3">مواقع طبية مهمة</h3>
              <p className="text-slate-500 leading-relaxed text-sm mb-6">
                مجموعة من أهم المراجع الطبية العالمية المجانية المفتوحة، مختارة بعناية لدعم القرار السريري السريع.
              </p>
            </div>
            <div className="flex items-center text-rose-600 font-bold text-sm gap-2 mt-auto">
              <span>تصفح المواقع</span>
              <ChevronRight className="w-4 h-4 group-hover:-translate-x-1 transition-transform rotate-180" />
            </div>
          </motion.div>


        </div>
      </div>
    </div>
  );
}
